/**
 * The deal list's reconciliation arithmetic (register P1).
 *
 * Pure, and deliberately kept out of the component file: the numbers Chris
 * asked to see stated on screen are the part worth asserting directly, without
 * mounting anything.
 */
import type { IntakeSurface } from '@/services/ingestion'

/** The disclosure region's id — distinct from Intake's, so both can be mounted. */
export const PIPELINE_RECONCILIATION_DOM_ID = 'pipeline-reconciliation-surfaces'

/**
 * The deal-list surface, chosen structurally rather than by label.
 *
 * `fromIntake` is documented on the contract as deal-list only, so it
 * identifies the row without depending on the display string staying
 * `"Deal list"` — renaming a label on screen must not silently blank this line.
 */
export function dealListSurface(surfaces: IntakeSurface[]): IntakeSurface | null {
  return surfaces.find((s) => typeof s.fromIntake === 'number') ?? null
}

/**
 * The sentence beneath the metric row, as clauses.
 *
 * Returned as parts rather than a string so the arithmetic is asserted directly
 * in tests, and so a caller can style a clause without parsing prose back apart.
 */
export function reconciliationClauses({
  onScreen,
  total,
  dealList,
}: {
  /** Rows the grid is showing right now, after the desk's view filters. */
  onScreen: number
  /** Every live intake record in this partition — the single number. */
  total: number
  dealList: IntakeSurface
}): string[] {
  const live = dealList.count
  const fromIntake = dealList.fromIntake ?? 0
  // The server states the other direction under `adds`; summing it here keeps
  // the two directions from being counted twice if it ever gains a second row.
  const outside = (dealList.adds ?? []).reduce((sum, a) => sum + a.records, 0)

  const clauses: string[] = []

  clauses.push(
    onScreen === live
      ? `${live} live pipeline row${live === 1 ? '' : 's'}`
      : `Showing ${onScreen} of ${live} live pipeline row${live === 1 ? '' : 's'}`,
  )

  if (outside > 0) {
    clauses.push(`${fromIntake} from intake, ${outside} entered outside it`)
  } else if (live > 0) {
    clauses.push(fromIntake === live ? `all from intake` : `${fromIntake} from intake`)
  }

  clauses.push(`intake holds ${total} record${total === 1 ? '' : 's'}`)

  return clauses
}
